import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import i18n from 'i18n-js';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import { Box, IconButton, MenuItem, Paper, Tab, Tabs, TextField } from '@material-ui/core';
import Finances from './components/finances';
import { theme } from 'config/theme';
import Tips from './components/tips';
import Storno from './components/storno';
import PaymentsTab from './components/payments';
import { useAppSelector } from 'index';
import restaurantDetailsStore from 'store/restaurantDetailsStore';
import { useDispatch } from 'react-redux';
import GroupSales from './components/groupSales';
import useStyles from './styles';
import GroupPayments from './components/groupPayments';
import { DateFilterType } from 'store/uiStore/interfaces';
import { actionCreators } from 'store/uiStore/actionCreators';
import dayjs from 'dayjs';

interface FinanceProps {
    restaurantId?: string;
    isOld?: boolean;
    style?: React.CSSProperties;
}

enum RestaurantView {
    Current,
    Group
}

enum FinanceView {
    SALES,
    PAYMENTS,
    TIPS,
    STORNO,
}

export default function Finance(props: FinanceProps) {
    const history = useHistory();
    const classes = useStyles();
    const dispatch = useDispatch();
    const groups = useAppSelector(state => state.restaurantDetails.managedGroups);
    const [view, setView] = useState<FinanceView>(FinanceView.SALES);
    const [restaurantView, setRestaurantView] = useState<RestaurantView>(RestaurantView.Current);
    const [groupId, setGroupId] = useState('');
    const { dateFilter } = useAppSelector((state) => state.ui.finance);

    useEffect(() => {
        setGroupId(groups[0]?.id || '');
    }, [groups]);

    useEffect(() => {
        if (!props.isOld) {
            dispatch(restaurantDetailsStore.actionCreators.getCurrentRestaurantManagedGroups());
        }
    }, []);

    function renderBackButton(styles?: React.CSSProperties) {
        return (
            <IconButton
                aria-label="delete"
                size="small"
                style={{ color: theme.palette.common.black, ...styles }}
                onClick={() => history.goBack()}
            >
                <ArrowBackIcon fontSize="inherit" />
            </IconButton>
        );
    }

    function renderCurrentTabs() {
        return (
            <Tabs
                id="hide-in-print"
                value={view}
                onChange={(_, newValue) => setView(newValue)}
                variant="scrollable"
                aria-label="wrapped label tabs example"
                indicatorColor="primary"
                textColor="primary"
            >
                <Tab label={i18n.t('navigationTabs.sales')} value={FinanceView.SALES} />
                <Tab label={i18n.t('navigationTabs.payments')} value={FinanceView.PAYMENTS} />
                {restaurantView !== RestaurantView.Group && (
                    <Tab label={i18n.t('navigationTabs.tips')} value={FinanceView.TIPS} />
                )}
                {restaurantView !== RestaurantView.Group && (
                    <Tab label={i18n.t('navigationTabs.storno')} value={FinanceView.STORNO} />
                )}
            </Tabs>
        );
    }

    function renderGroupTabs(styles?: React.CSSProperties) {
        return (
            <Tabs
                id="hide-in-print"
                value={restaurantView}
                onChange={(_, newValue) => {
                    setRestaurantView(newValue);
                    if (
                        newValue === RestaurantView.Group &&
                        view !== FinanceView.SALES &&
                        view !== FinanceView.PAYMENTS
                    ) {
                        setView(FinanceView.SALES);
                    }
                    if (dateFilter === DateFilterType.CLOSING) {
                        dispatch(actionCreators.setFinanceDateFilter(DateFilterType.DAY));
                        dispatch(actionCreators.setFinanceDateHistory([]));
                        dispatch(
                            actionCreators.setFinanceDateRange(
                                dayjs().startOf('day').toDate(),
                                dayjs().endOf('day').toDate()
                            )
                        );
                    }
                }}
                variant="scrollable"
                aria-label="restaurant view toggle"
                indicatorColor="primary"
                textColor="primary"
                style={styles}
            >
                <Tab
                    label={i18n.t('navigationTabs.currentRestaurant')}
                    value={RestaurantView.Current}
                />
                <Tab
                    label={i18n.t('navigationTabs.restaurantGroup')}
                    value={RestaurantView.Group}
                />
            </Tabs>
        );
    }

    function renderCurrentRestaurantView() {
        return (
            <>
                {view === FinanceView.SALES && <Finances {...props} />}
                {view === FinanceView.PAYMENTS && <PaymentsTab {...props} />}
                {view === FinanceView.TIPS && <Tips />}
                {view === FinanceView.STORNO && <Storno />}
            </>
        );
    }

    function renderGroupView() {
        return (
            <>
                {view === FinanceView.SALES && <GroupSales groupId={groupId} {...props} />}
                {view === FinanceView.PAYMENTS && <GroupPayments groupId={groupId} {...props} />}
            </>
        );
    }

    function renderSelectGroup(styles?: React.CSSProperties) {
        return (
            <TextField
                select
                size="small"
                variant="outlined"
                style={{ width: 200, ...styles }}
                value={groupId}
                onChange={(e) => setGroupId(e.target.value)}
            >
                {groups.map((option) => (
                    <MenuItem key={option.id} value={option.id}>
                        {option.name}
                    </MenuItem>
                ))}
            </TextField>
        );
    }

    if (groups.length > 0) {
        return (
            <Paper style={{ overflow: 'hidden', paddingTop: 105, ...props.style }}>
                <Box
                    display={'flex'}
                    justifyContent={'flex-start'}
                    flexDirection={'column'}
                    className={classes.tabsContainer}
                    style={{ paddingTop: 0 }}
                >
                    <Box display={'flex'} alignItems={'center'} style={{ marginBottom: 8 }}>
                        {renderBackButton()}
                        {renderGroupTabs({ flex: 1 })}
                        {restaurantView === RestaurantView.Group && renderSelectGroup({ marginTop: 8 })}
                    </Box>
                    {renderCurrentTabs()}
                </Box>
                {restaurantView === RestaurantView.Current
                    ? renderCurrentRestaurantView()
                    : renderGroupView()}
            </Paper>
        );
    }

    return (
        <Paper style={{ overflow: 'hidden', paddingTop: 50, ...props.style }}>
            <Box
                display={'flex'}
                justifyContent={'flex-start'}
                className={classes.tabsContainer}
            >
                {renderBackButton()}
                {renderCurrentTabs()}
            </Box>
            {renderCurrentRestaurantView()}
        </Paper>
    );
}
